import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  ShieldCheck, Loader2, PlayCircle, Download, ArrowLeft, X, Landmark, FileCheck2,
  Database, Building2, AlertTriangle, BookOpenText, FileSearch, Globe2, Lock,
} from "lucide-react";
import { ClassificationAssistant, MfaTrack, PrivacyRequestsAdmin, SecurityDocsAdmin } from "@/components/compliance/Phase2Panels";
import { IncidentsPanel, LegalHoldsPanel, ScorecardPanel } from "@/components/compliance/Phase3Panels";
import { ExecutiveDashboard, MaturityPanel, WhitepapersPanel, AuditModePanel, LineagePanel } from "@/components/compliance/Phase4Panels";
import { HealthPanel, OperationsPanel, PolicyLifecyclePanel, TrainingPanel } from "@/components/compliance/OpsPanels";

const DISCLAIMER = "This dashboard reflects technical, administrative and operational control status. It is not a legal opinion, independent audit report, certification or guarantee of compliance.";
const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30";

const STATUS_TONE = {
  Passing: "bg-emerald-50 text-emerald-600", Warning: "bg-amber-50 text-amber-600",
  Failing: "bg-red-50 text-red-600", "Not Tested": "bg-slate-100 text-slate-500",
  "Not Applicable": "bg-slate-100 text-slate-400", "Legal Review Required": "bg-purple-50 text-purple-600",
  "External Assessment Required": "bg-indigo-50 text-indigo-600", "Auditor Verified": "bg-emerald-100 text-emerald-700",
  Expired: "bg-red-100 text-red-700", Active: "bg-emerald-50 text-emerald-600",
  "Research Required": "bg-amber-50 text-amber-600", "Pending Legal Review": "bg-purple-50 text-purple-600",
  "Attorney Reviewed": "bg-emerald-50 text-emerald-600", open: "bg-red-50 text-red-600",
};
const Badge = ({ v }) => <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_TONE[v] || "bg-slate-100 text-slate-500"}`}>{String(v ?? "—")}</span>;

const SOC2_STATUSES = ["Not Started", "Readiness Planning", "Controls Being Implemented", "Evidence Collection",
  "Readiness Assessment Underway", "Remediation Underway", "Auditor Selection", "Type I Examination Underway",
  "Type I Report Issued", "Type II Observation Period", "Type II Examination Underway", "Type II Report Issued",
  "Report Expired", "Exception or Qualification Under Review"];

const SECTIONS = {
  soc2: { label: "SOC 2 Readiness", icon: FileCheck2, cols: [["tsc_ref", "TSC"], ["description", "Control"], ["result", "Result", "badge"], ["result_type", "Result type"], ["owner", "Owner"], ["last_tested_at", "Last tested", "date"]] },
  hipaa: { label: "HIPAA Readiness", icon: ShieldCheck, cols: [["family", "Family"], ["name", "Safeguard"], ["citation", "Citation"], ["implementation_status", "Status", "badge"], ["external_review_status", "External review", "badge"]] },
  rules: { label: "State Law Map", icon: Globe2, cols: [["jurisdiction", "Juris."], ["law_name", "Law"], ["rule_type", "Type"], ["status", "Status", "badge"], ["version", "v"], ["attorney_reviewed", "Atty ✓"], ["next_review_due", "Review due", "date"]] },
  inventory: { label: "Data Inventory", icon: Database, cols: [["asset_type", "Type"], ["name", "Asset"], ["classification", "Classification"], ["review_status", "Review"]] },
  vendors: { label: "Vendors", icon: Building2, cols: [["vendor", "Vendor"], ["service", "Service"], ["risk_rating", "Risk"], ["baa_required", "BAA req."], ["baa_on_file", "BAA ✓"], ["approved_for_restricted_data", "Restricted OK"]] },
  risks: { label: "Risk Register", icon: AlertTriangle, cols: [["description", "Risk"], ["category", "Category"], ["likelihood", "Likelihood"], ["impact", "Impact"], ["residual_risk", "Residual"], ["status", "Status", "badge"]] },
  retention: { label: "Retention", icon: BookOpenText, cols: [["data_type", "Data type"], ["rule", "Rule"], ["legal_basis", "Basis"], ["status", "Status", "badge"]] },
  ai: { label: "AI Governance", icon: FileSearch, cols: [["name", "System"], ["provider", "Provider"], ["purpose", "Purpose"], ["human_review_required", "Human review"], ["status", "Status", "badge"]] },
  policies: { label: "Policies", icon: BookOpenText, cols: [["name", "Policy"], ["version", "Version"], ["status", "Status"], ["owner", "Owner"], ["effective_date", "Effective"]] },
  evidence: { label: "Evidence", icon: FileSearch, cols: [["description", "Evidence"], ["evidence_type", "Type"], ["source", "Source"], ["approved", "Approved"], ["collected_at", "Collected", "date"]] },
  audit: { label: "Audit Log", icon: Lock, cols: [["event", "Event"], ["actor_name", "Actor"], ["at", "At", "date"]] },
};

const fmtDate = (v) => v ? new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

export default function ComplianceCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [checkRun, setCheckRun] = useState(null);
  const [running, setRunning] = useState(false);

  const loadDash = useCallback(() => api.get("/compliance/dashboard").then(({ data }) => setDash(data)).catch(() => {}), []);
  useEffect(() => {
    if (user && !user.super_admin) { navigate("/admin"); return; }
    api.post("/compliance/seed").catch(() => {})
      .finally(() => {
        api.post("/compliance/lock/seed").catch(() => {});
        api.post("/compliance/phase4/seed").catch(() => {}).finally(loadDash);
      });
  }, [user, navigate, loadDash]);

  const loadTab = useCallback((t, status = null) => {
    setTab(t); setRows(null); setFilterStatus(status);
    if (SECTIONS[t]) {
      api.get(`/compliance/${t}/records`, { params: status ? { status } : {} })
        .then(({ data }) => setRows(data)).catch(() => setRows([]));
    }
  }, []);

  const runChecks = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/compliance/run-checks");
      setCheckRun(data); setTab("overview"); loadDash();
      const fails = data.results.filter((r) => r.result === "Failing").length;
      toast[fails ? "warning" : "success"](`${data.results.length} automated checks completed — ${fails} failing (results stay visible; automated checks never establish legal compliance).`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Check run failed"); }
    finally { setRunning(false); }
  };

  if (!dash) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-7 h-7 text-cobalt animate-spin" /></div>;
  const c = dash.cards;

  const CARDS = [
    ["SOC 2 Program Status", c.soc2_status, () => loadTab("soc2"), "soc2-status"],
    ["Controls Not Tested", c.controls_not_tested, () => loadTab("soc2", "Not Tested"), "controls-not-tested"],
    ["Failed Controls", c.failed_controls, () => loadTab("soc2", "Failing"), "failed-controls"],
    ["State Rules Needing Review", c.state_rules_needing_review, () => loadTab("rules"), "rules-review"],
    ["HIPAA Safeguards Open", c.hipaa_safeguards_open, () => loadTab("hipaa", "Not Tested"), "hipaa-open"],
    ["Inventory Pending Classification", c.inventory_unclassified, () => loadTab("inventory", "seeded — human review required"), "inventory-unclassified"],
    ["Vendor Reviews Due", c.vendor_reviews_due, () => loadTab("vendors"), "vendor-reviews"],
    ["Missing BAAs / DPAs", c.expiring_baas_dpas, () => loadTab("vendors"), "missing-baas"],
    ["Open Risks", c.open_risks, () => loadTab("risks", "open"), "open-risks"],
    ["Policies in Draft", c.policies_in_draft, () => loadTab("policies"), "policies-draft"],
    ["Open Privacy Requests", c.open_privacy_requests, () => setTab("privacy"), "privacy-requests"],
    ["Incidents / Legal Holds", `${c.open_incidents} / ${c.active_legal_holds}`, () => setTab("incidents"), "incidents-holds"],
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="compliance-center">
      <header className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link to="/admin" className="text-white/50 text-xs flex items-center gap-1 hover:text-white" data-testid="cc-back"><ArrowLeft className="w-3.5 h-3.5" /> Admin Portal</Link>
          <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
            <div>
              <h1 className="font-heading text-2xl font-extrabold tracking-tight flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-cobalt" /> COMPLIANCE &amp; SECURITY COMMAND CENTER</h1>
              <p className="text-white/60 text-xs mt-1 max-w-3xl">{DISCLAIMER}</p>
            </div>
            <button onClick={runChecks} disabled={running} data-testid="cc-run-checks"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-cobalt text-white text-sm font-bold disabled:opacity-50">
              <PlayCircle className="w-4 h-4" /> {running ? "Running…" : "RUN COMPLIANCE CHECKS NOW"}
            </button>
            <Link to="/enterprise-readiness" data-testid="cc-readiness-link"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 text-white text-sm font-bold hover:bg-white/20">
              <Building2 className="w-4 h-4" /> Enterprise Readiness Center
            </Link>
            <Link to="/security-suite" data-testid="cc-suite-link"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 text-white text-sm font-bold hover:bg-white/20">
              <ShieldCheck className="w-4 h-4" /> Security Suite
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* readiness strip */}
        <div className="rounded-3xl bg-white border border-slate-100 p-5 mb-5" data-testid="cc-readiness">
          <div className="flex flex-wrap gap-8 items-center">
            <div><div className="text-[10px] uppercase font-bold text-slate-400">Controls implemented (tested)</div>
              <div className="font-heading text-2xl font-extrabold text-navy">{c.overall_readiness.implemented_pct}%</div></div>
            <div><div className="text-[10px] uppercase font-bold text-slate-400">Controls operating (passing)</div>
              <div className="font-heading text-2xl font-extrabold text-navy">{c.overall_readiness.operating_pct}%</div></div>
            <div><div className="text-[10px] uppercase font-bold text-slate-400">Approved evidence</div>
              <div className="font-heading text-2xl font-extrabold text-navy">{c.overall_readiness.approved_evidence_items}</div></div>
            <div><div className="text-[10px] uppercase font-bold text-slate-400">Externally verified</div>
              <div className="font-heading text-2xl font-extrabold text-navy">{c.overall_readiness.externally_verified}</div></div>
            <p className="text-[11px] text-slate-400 max-w-md">{c.overall_readiness.formula}</p>
          </div>
        </div>

        {/* cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          {CARDS.map(([label, value, onClick, tid]) => (
            <button key={tid} onClick={onClick || undefined} disabled={!onClick} data-testid={`cc-card-${tid}`}
              className={`text-left rounded-2xl border p-3.5 bg-white ${onClick ? "border-slate-100 hover:border-cobalt/40 hover:shadow-md transition-all" : "border-dashed border-slate-200 opacity-70"}`}>
              <div className="text-[10px] font-bold uppercase text-slate-400 leading-tight">{label}</div>
              <div className="font-heading text-lg font-extrabold text-navy mt-1 truncate">{String(value)}</div>
            </button>
          ))}
        </div>

        {/* tabs */}
        <div className="flex gap-1.5 flex-wrap mb-4">
          <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} label="Overview" testid="cc-tab-overview" />
          <TabBtn active={tab === "health"} onClick={() => setTab("health")} label="Health Score" testid="cc-tab-health" />
          <TabBtn active={tab === "executive"} onClick={() => setTab("executive")} label="Executive Dashboard" testid="cc-tab-executive" />
          {Object.entries(SECTIONS).map(([k, s]) => (
            <TabBtn key={k} active={tab === k} onClick={() => loadTab(k)} label={s.label} testid={`cc-tab-${k}`} />
          ))}
          <TabBtn active={tab === "incidents"} onClick={() => setTab("incidents")} label="Incidents & Breach" testid="cc-tab-incidents" />
          <TabBtn active={tab === "holds"} onClick={() => setTab("holds")} label="Legal Holds" testid="cc-tab-holds" />
          <TabBtn active={tab === "scorecard"} onClick={() => setTab("scorecard")} label="Scorecard & Crosswalk" testid="cc-tab-scorecard" />
          <TabBtn active={tab === "maturity"} onClick={() => setTab("maturity")} label="Maturity" testid="cc-tab-maturity" />
          <TabBtn active={tab === "operations"} onClick={() => setTab("operations")} label="Operations (BC/DR)" testid="cc-tab-operations" />
          <TabBtn active={tab === "policylifecycle"} onClick={() => setTab("policylifecycle")} label="Policy Lifecycle" testid="cc-tab-policylifecycle" />
          <TabBtn active={tab === "training"} onClick={() => setTab("training")} label="Training" testid="cc-tab-training" />
          <TabBtn active={tab === "privacy"} onClick={() => setTab("privacy")} label="Privacy Requests" testid="cc-tab-privacy" />
          <TabBtn active={tab === "classification"} onClick={() => setTab("classification")} label="Classification Assistant" testid="cc-tab-classification" />
          <TabBtn active={tab === "mfa"} onClick={() => setTab("mfa")} label="MFA Track" testid="cc-tab-mfa" />
          <TabBtn active={tab === "secdocs"} onClick={() => setTab("secdocs")} label="Security Docs" testid="cc-tab-secdocs" />
          <TabBtn active={tab === "whitepapers"} onClick={() => setTab("whitepapers")} label="Whitepapers" testid="cc-tab-whitepapers" />
          <TabBtn active={tab === "auditmode"} onClick={() => setTab("auditmode")} label="Audit Mode" testid="cc-tab-auditmode" />
          <TabBtn active={tab === "lineage"} onClick={() => setTab("lineage")} label="Data Lineage" testid="cc-tab-lineage" />
          <TabBtn active={tab === "trust"} onClick={() => setTab("trust")} label="Trust Center Admin" testid="cc-tab-trust" />
          <TabBtn active={tab === "reports"} onClick={() => setTab("reports")} label="Reports" testid="cc-tab-reports" />
        </div>

        {tab === "overview" && <Overview checkRun={checkRun} dash={dash} onOpenSoc2={() => loadTab("soc2")} />}
        {tab === "soc2" && <Soc2Program status={c.soc2_status} onChanged={loadDash} />}
        {SECTIONS[tab] && (
          <RecordsTable section={tab} rows={rows} filterStatus={filterStatus}
            onRow={(r) => setDetail({ section: tab, row: r })} onClearFilter={() => loadTab(tab)} />
        )}
        {tab === "trust" && <TrustAdmin />}
        {tab === "privacy" && <PrivacyRequestsAdmin />}
        {tab === "incidents" && <IncidentsPanel />}
        {tab === "holds" && <LegalHoldsPanel />}
        {tab === "scorecard" && <ScorecardPanel />}
        {tab === "executive" && <ExecutiveDashboard />}
        {tab === "health" && <HealthPanel onNavigate={(t) => (SECTIONS[t] ? loadTab(t) : setTab(t))} />}
        {tab === "operations" && <OperationsPanel />}
        {tab === "policylifecycle" && <PolicyLifecyclePanel />}
        {tab === "training" && <TrainingPanel />}
        {tab === "maturity" && <MaturityPanel />}
        {tab === "whitepapers" && <WhitepapersPanel />}
        {tab === "auditmode" && <AuditModePanel />}
        {tab === "lineage" && <LineagePanel />}
        {tab === "classification" && <ClassificationAssistant />}
        {tab === "mfa" && <MfaTrack />}
        {tab === "secdocs" && <SecurityDocsAdmin />}
        {tab === "reports" && <Reports />}
      </div>

      {detail && <DetailDrawer detail={detail} onClose={() => setDetail(null)} onSaved={() => { setDetail(null); loadTab(detail.section, filterStatus); loadDash(); }} />}
    </div>
  );
}

const TabBtn = ({ active, onClick, label, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${active ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-navy/30"}`}>{label}</button>
);

const Overview = ({ checkRun, dash, onOpenSoc2 }) => (
  <div className="space-y-4" data-testid="cc-overview">
    <div className="rounded-3xl bg-white border border-slate-100 p-6">
      <h3 className="font-heading font-bold text-navy flex items-center gap-2"><Landmark className="w-4 h-4 text-cobalt" /> Program posture</h3>
      <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
        <div className="rounded-2xl bg-slate-50 p-4"><div className="text-[10px] uppercase font-bold text-slate-400">SOC 2</div><div className="font-semibold text-navy mt-1">{dash.cards.soc2_status}</div><button onClick={onOpenSoc2} className="text-cobalt text-xs font-semibold mt-1">Manage →</button></div>
        <div className="rounded-2xl bg-slate-50 p-4"><div className="text-[10px] uppercase font-bold text-slate-400">HIPAA</div><div className="font-semibold text-navy mt-1 text-xs">{dash.cards.hipaa_posture}</div></div>
        <div className="rounded-2xl bg-slate-50 p-4"><div className="text-[10px] uppercase font-bold text-slate-400">Trust Center</div><div className="font-semibold text-navy mt-1">{dash.cards.trust_center_published ? "Published" : "Not published (held for your review)"}</div></div>
      </div>
    </div>
    {checkRun && (
      <div className="rounded-3xl bg-white border border-slate-100 p-6" data-testid="cc-check-results">
        <h3 className="font-heading font-bold text-navy">Latest automated check run</h3>
        <p className="text-[11px] text-slate-400 mt-0.5">{checkRun.note}</p>
        <div className="mt-3 space-y-2">
          {checkRun.results.map((r, i) => (
            <div key={i} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-2.5 text-xs">
              <div><b className="text-navy">{r.control}</b> — {r.check}<div className="text-slate-400 mt-0.5">{r.detail}</div></div>
              <Badge v={r.result} />
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

const Soc2Program = ({ status, onChanged }) => {
  const [sel, setSel] = useState(status);
  const [err, setErr] = useState(null);
  const attested = sel === "Type I Report Issued" || sel === "Type II Report Issued";
  const [att, setAtt] = useState({});
  const save = async () => {
    setErr(null);
    try {
      await api.put("/compliance/program/soc2-status", { status: sel, attestation: att });
      toast.success(`SOC 2 status set: ${sel}`); onChanged();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.missing_required_documentation) setErr(d);
      else toast.error(formatApiErrorDetail(d) || "Failed");
    }
  };
  return (
    <div className="rounded-3xl bg-white border border-slate-100 p-6 mb-4" data-testid="cc-soc2-program">
      <h3 className="font-heading font-bold text-navy">SOC 2 program status</h3>
      <p className="text-[11px] text-slate-400 mt-0.5">"Report Issued" statuses are blocked until complete external-auditor documentation is recorded (representation rule — enforced server-side).</p>
      <div className="mt-3 flex flex-wrap gap-2 items-center">
        <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid="cc-soc2-select" className={`${inputCls} max-w-xs`}>
          {SOC2_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <button onClick={save} data-testid="cc-soc2-save" className="px-5 py-2 rounded-full bg-navy text-white text-xs font-bold">Update status</button>
      </div>
      {attested && (
        <div className="mt-3 grid sm:grid-cols-3 gap-2">
          {["auditor_name", "report_type", "covered_system", "covered_services", "tsc_included", "period_or_as_of_date", "issue_date", "expiration_or_review_date", "authorized_public_description", "report_file_id", "auditor_contact"].map((f) => (
            <input key={f} placeholder={f.replace(/_/g, " ")} value={att[f] || ""} onChange={(e) => setAtt({ ...att, [f]: e.target.value })} data-testid={`cc-att-${f}`} className={inputCls} />
          ))}
          <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={!!att.management_approved} onChange={(e) => setAtt({ ...att, management_approved: e.target.checked })} data-testid="cc-att-mgmt" className="accent-cobalt" /> Management approval recorded</label>
        </div>
      )}
      {err && (
        <div className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-600" data-testid="cc-soc2-blocked">
          <b>{err.message}</b>
          <div className="mt-1">Missing: {err.missing_required_documentation.join(", ")}</div>
        </div>
      )}
    </div>
  );
};

const RecordsTable = ({ section, rows, filterStatus, onRow, onClearFilter }) => {
  const s = SECTIONS[section];
  if (rows === null) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  const filtered = filterStatus ? rows.filter((r) => [r.result, r.status, r.implementation_status, r.review_status].includes(filterStatus)) : rows;
  return (
    <div className="rounded-3xl bg-white border border-slate-100 overflow-hidden" data-testid={`cc-table-${section}`}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <div className="font-heading font-bold text-navy text-sm">{s.label} <span className="text-slate-300 font-normal">({filtered.length})</span></div>
        {filterStatus && <button onClick={onClearFilter} data-testid="cc-clear-filter" className="text-xs text-cobalt font-semibold">Filtered: {filterStatus} — clear ×</button>}
      </div>
      <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-slate-50"><tr>{s.cols.map(([k, label]) => <th key={k} className="text-left px-4 py-2 font-bold text-slate-400 uppercase text-[10px]">{label}</th>)}</tr></thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={i} onClick={() => onRow(r)} className="border-t border-slate-50 hover:bg-cobalt/5 cursor-pointer" data-testid={`cc-row-${section}-${i}`}>
                {s.cols.map(([k, , kind]) => (
                  <td key={k} className="px-4 py-2.5 text-slate-600 max-w-[320px] truncate">
                    {kind === "badge" ? <Badge v={r[k]} /> : kind === "date" ? fmtDate(r[k]) :
                      typeof r[k] === "boolean" ? (r[k] ? "✓" : "—") : String(r[k] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EDITABLE = {
  soc2: ["owner", "operator", "result", "result_type", "remediation", "frequency"],
  hipaa: ["owner", "implementation_status", "result_type", "remediation", "auditor_notes"],
  rules: ["status", "internal_owner", "legal_reviewer", "attorney_reviewed", "notes", "confidence_level", "change_reason"],
  inventory: ["classification", "review_status", "description"],
  vendors: ["risk_rating", "approved_for_restricted_data", "baa_on_file", "dpa_on_file", "contract_on_file", "owner"],
  risks: ["status", "owner", "treatment", "residual_risk", "likelihood", "impact", "target_remediation_date"],
  policies: ["owner", "version", "status", "effective_date"],
};

const DetailDrawer = ({ detail, onClose, onSaved }) => {
  const { section, row } = detail;
  const idField = { soc2: "control_id", hipaa: "safeguard_id", rules: "rule_id", inventory: "asset_id", vendors: "vendor_id", risks: "risk_id", policies: "policy_id", evidence: "evidence_id", audit: "audit_id" }[section];
  const editable = EDITABLE[section] || [];
  const [form, setForm] = useState(() => Object.fromEntries(editable.map((f) => [f, row[f] ?? ""])));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/compliance/${section}/records/${row[idField]}`, form);
      toast.success("Record updated (audited)"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed"); }
    finally { setSaving(false); }
  };
  const approveEvidence = async () => {
    try { await api.post(`/compliance/evidence/${row.evidence_id}/approve`); toast.success("Evidence approved — now immutable"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="cc-detail-drawer">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-navy text-sm">{SECTIONS[section]?.label} — record</h3>
          <button onClick={onClose} data-testid="cc-drawer-close" className="p-1.5 rounded-full hover:bg-slate-100"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="rounded-2xl bg-slate-50 p-4 text-[11px] font-mono text-slate-600 space-y-1 max-h-72 overflow-y-auto">
          {Object.entries(row).filter(([k]) => !["versions", "handling", "flow", "content"].includes(k)).map(([k, v]) => (
            <div key={k}><span className="text-slate-400">{k}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v ?? "—")}</div>
          ))}
        </div>
        {section === "evidence" && !row.approved && (
          <button onClick={approveEvidence} data-testid="cc-approve-evidence" className="mt-3 w-full py-2.5 rounded-full bg-emerald-600 text-white text-xs font-bold">Approve evidence (locks it — immutable)</button>
        )}
        {editable.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] font-bold text-slate-400 uppercase mb-2">Edit {section === "rules" ? "(creates a new version — change_reason required)" : "(audited)"}</div>
            <div className="space-y-2">
              {editable.map((f) => (
                typeof row[f] === "boolean" || ["attorney_reviewed", "approved_for_restricted_data", "baa_on_file", "dpa_on_file", "contract_on_file"].includes(f) ? (
                  <label key={f} className="flex items-center gap-2 text-xs text-slate-600">
                    <input type="checkbox" checked={!!form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.checked })} data-testid={`cc-edit-${f}`} className="accent-cobalt" /> {f.replace(/_/g, " ")}
                  </label>
                ) : (
                  <div key={f}><label className="text-[10px] font-semibold text-slate-400 uppercase">{f.replace(/_/g, " ")}</label>
                    <input value={form[f] ?? ""} onChange={(e) => setForm({ ...form, [f]: e.target.value })} data-testid={`cc-edit-${f}`} className={`${inputCls} mt-0.5`} /></div>
                )
              ))}
            </div>
            <button onClick={save} disabled={saving} data-testid="cc-save-record" className="mt-3 w-full py-2.5 rounded-full bg-navy text-white text-xs font-bold disabled:opacity-50">{saving ? "Saving…" : "Save changes"}</button>
          </div>
        )}
      </div>
    </div>
  );
};

const TrustAdmin = () => {
  const [tc, setTc] = useState(null);
  useEffect(() => { api.get("/compliance/trust-center").then(({ data }) => setTc(data.trust_center)).catch(() => {}); }, []);
  if (!tc) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 text-cobalt animate-spin" /></div>;
  const toggle = async () => {
    try {
      const { data } = await api.put("/compliance/trust-center", { published: !tc.published });
      setTc(data.trust_center);
      toast.success(data.trust_center.published ? "Trust Center PUBLISHED — now visible at /trust-center" : "Trust Center unpublished — hidden from the public");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="space-y-4" data-testid="cc-trust-admin">
      <div className={`rounded-3xl border p-6 ${tc.published ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading font-bold text-navy">Public Security &amp; Trust Center</h3>
            <p className="text-xs text-slate-500 mt-1">Status: <b>{tc.published ? "PUBLISHED (publicly visible)" : "NOT PUBLISHED — held for your review"}</b>. Preview it privately at <Link to="/trust-center" className="text-cobalt font-semibold underline">/trust-center</Link> (as Super Admin you always see the preview).</p>
          </div>
          <button onClick={toggle} data-testid="cc-trust-publish" className={`px-5 py-2.5 rounded-full text-white text-xs font-bold ${tc.published ? "bg-red-500" : "bg-navy"}`}>
            {tc.published ? "Unpublish" : "Publish Trust Center"}
          </button>
        </div>
      </div>
      <div className="rounded-3xl bg-white border border-slate-100 p-6">
        <h4 className="font-heading font-semibold text-navy text-sm">Approved badges (validated basis required)</h4>
        <div className="mt-2 flex flex-wrap gap-2">
          {(tc.badges || []).map((b) => <span key={b.key} className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 text-xs font-semibold">✓ {b.label}</span>)}
        </div>
        <h4 className="font-heading font-semibold text-navy text-sm mt-4">Public sections</h4>
        <div className="mt-2 grid sm:grid-cols-2 gap-2 text-xs text-slate-600">
          {Object.entries(tc.sections || {}).map(([k, v]) => <div key={k} className="rounded-xl bg-slate-50 p-3"><b className="text-navy uppercase text-[10px]">{k.replace(/_/g, " ")}</b><div className="mt-0.5">{v}</div></div>)}
        </div>
      </div>
    </div>
  );
};

const Reports = () => {
  const dl = async (kind) => {
    try {
      const res = await api.get(`/compliance/reports/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `crash_ready_${kind}_report.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Report failed"); }
  };
  return (
    <div className="rounded-3xl bg-white border border-slate-100 p-6" data-testid="cc-reports">
      <h3 className="font-heading font-bold text-navy">Reports &amp; auditor exports</h3>
      <p className="text-[11px] text-slate-400 mt-1">Every report embeds the required disclaimer: technical/administrative control status only — not a legal opinion, audit report, certification or guarantee of compliance.</p>
      <div className="mt-4 grid sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {[["soc2", "SOC 2 Readiness"], ["hipaa", "HIPAA Readiness"], ["rules", "State Privacy Coverage"], ["vendors", "Vendor Risk"], ["risks", "Risk Register"], ["inventory", "Data Inventory"], ["policies", "Policy Status"]].map(([k, label]) => (
          <button key={k} onClick={() => dl(k)} data-testid={`cc-report-${k}`}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:border-cobalt hover:text-cobalt">
            <Download className="w-3.5 h-3.5" /> {label} (CSV)
          </button>
        ))}
      </div>
    </div>
  );
};
